import React, {useMemo, useState} from 'react';
import {
    Box,
    Divider,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Text,
    Badge,
    HStack,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription
} from '@chakra-ui/react';
import {DataGrid} from '../../components/data-grid/data-grid';
import ConfigChangeList from './ConfigChangeList';
import {useConfigurationAudit} from './hooks/useConfigurationAudit';
import {useConfigurationEffectiveValue} from './hooks/useConfigurationEffectiveValue';
import {useConfigurationSave} from './hooks/useConfigurationSave';
import {buildConfigurationColumnDefs, buildConfigurationRowData} from './config-helpers';
import {GridOptions} from '@codametrix/grid';

interface CaseConfigDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    configDetail: CMxAPI.Configuration | null;
    serviceLines: CMxAPI.ServiceLine[];
    tenantId: string;
}

const ConfigurationDetailDrawer: React.FC<CaseConfigDrawerProps> = ({
                                                                        isOpen,
                                                                        onClose,
                                                                        configDetail,
                                                                        serviceLines,
                                                                        tenantId
                                                                    }) => {
    const [editingServiceLine, setEditingServiceLine] = useState<string | null>(null);
    const isSystemLevel = tenantId === '*';

    // Use the extracted hooks
    const { configurationAuditData, handlePageChange } = useConfigurationAudit({
        isOpen,
        tenantId,
        configKeyname: configDetail?.keyname
    });

    const { getEffectiveValueForServiceLine } = useConfigurationEffectiveValue(
        configDetail,
        tenantId
    );

    const { handleSaveRow } = useConfigurationSave(
        configDetail,
        tenantId,
        () => setEditingServiceLine(null)
    );

    // Show service line rows for both system level and tenant level
    const rowData = useMemo(() =>
            buildConfigurationRowData(
                configDetail,
                serviceLines, // Always pass service lines
                tenantId,
                getEffectiveValueForServiceLine,
                isSystemLevel
            ),
        [configDetail, serviceLines, tenantId, getEffectiveValueForServiceLine, isSystemLevel]
    );

    // Clean columnDefs using the extracted utility function
    const columnDefs = useMemo(() =>
            buildConfigurationColumnDefs(
                editingServiceLine,
                setEditingServiceLine,
                handleSaveRow,
                isSystemLevel
            ),
        [editingServiceLine, handleSaveRow, isSystemLevel]
    );

    const gridOptions: GridOptions = useMemo(
        () => ({
            rowHeight: 120,
            suppressCellFocus: true,
            pagination: false,
            paginationPageSize: undefined
        }),
        []
    );

    return (
        <Drawer
            isOpen={isOpen}
            placement="right"
            size="xl"
            onClose={onClose}
            blockScrollOnMount={false}>
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader pr="5rem">
                    <HStack spacing={3}>
                        <Text>
                            {configDetail?.displayName ||
                                configDetail?.keyname ||
                                'Configuration'}
                        </Text>
                        {isSystemLevel && (
                            <Badge colorScheme="purple" fontSize="0.8em">
                                System Level
                            </Badge>
                        )}
                    </HStack>
                </DrawerHeader>
                <DrawerBody>
                    {configDetail ? (
                        <>
                            {isSystemLevel && (
                                <Alert status="warning" mb={4} borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        <AlertTitle fontSize="sm">System-Level Configuration</AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            You are viewing system-level configurations that affect all tenants.
                                            Changes should be made carefully as they will impact the entire system.
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            <Box display="flex"
                                flexDirection="column"
                                flex="1 1 0"
                                minHeight={0}
                                width="100%"
                                height="100%" maxH="100%" overflowY="auto" mb={6}>
                                <DataGrid
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    gridOptions={gridOptions}
                                />
                            </Box>
                            <Divider mb={10} />
                  
                            <Box mb={5}>
                                <Text fontSize="sm" fontWeight="bold" mb={1}>
                                    DESCRIPTION
                                </Text>
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                    {configDetail.keyname}
                                </Text>
                                <Text>
                                    {configDetail.description || 'No description provided.'}
                                </Text>
                            </Box>

                            <Divider mb={10} />

                            <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>
                                    CHANGE HISTORY
                                </Text>
                                <ConfigChangeList
                                    changes={configurationAuditData.content}
                                    auditData={configurationAuditData}
                                    onPageChange={handlePageChange}
                                />
                            </Box>
                        </>
                    ) : (
                        <Text>Loading configuration details...</Text>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

export default ConfigurationDetailDrawer;